import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Logo } from "./Logo";
import MagneticButton from "./MagneticButton";
import ParticleBackground from "./ParticleBackground";

gsap.registerPlugin(ScrollTrigger);

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/548142d1-77fb-4f3c-9110-134fa2805c36/images/6d630ac9bcf4393e90d5d0f6c45e0a201ed0f87f1ad5f35095059c66198ebf00.png";

const Hero = () => {
  const { t } = useLang();

  const rootRef = useRef(null);
  const logoRef = useRef(null);
  const titleRef = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Entrance animation
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.from(logoRef.current, {
        opacity: 0,
        scale: 0.6,
        y: 20,
        duration: 1.2,
      })
        .from(
          titleRef.current,
          {
            opacity: 0,
            y: 40,
            duration: 0.9,
          },
          "-=0.6"
        )
        .from(
          subRef.current,
          {
            opacity: 0,
            y: 30,
            duration: 0.8,
          },
          "-=0.55"
        )
        .from(
          ctaRef.current,
          {
            opacity: 0,
            y: 20,
            duration: 0.7,
          },
          "-=0.5"
        );
    }, rootRef);

    return () => ctx.revert();
  }, []);

  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      ref={rootRef}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />

      <div
        className="absolute inset-0 bg-gradient-to-b from-[#030407]/85 via-[#030407]/70 to-[#030407]"
        aria-hidden="true"
      />

      <div
        className="absolute inset-0 bg-grid opacity-50"
        aria-hidden="true"
      />

      {/* Particles */}
      <ParticleBackground density={80} />

      {/* Glow effects */}
      <div
        className="radial-blob"
        style={{
          width: 520,
          height: 520,
          background: "#00F0FF",
          top: "10%",
          left: "-10%",
        }}
      />

      <div
        className="radial-blob"
        style={{
          width: 620,
          height: 620,
          background: "#0055FF",
          bottom: "-15%",
          right: "-12%",
          opacity: 0.4,
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full text-center  flex flex-col gap-4">
        {/* Logo */}
        <div
          ref={logoRef}
          className="flex justify-center mb-2"
          data-testid="hero-logo-wrapper"
        >
          <Logo size="hero" />
        </div>

        {/* Title */}
        <h1
          ref={titleRef}
          data-testid="hero-title"
         className="font-sans font-extrabold tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl gradient-text max-w-4xl mx-auto leading-[1.1]"
        >
          {t.hero.title}
        </h1>

        {/* Subtitle */}
        <p
          ref={subRef}
          data-testid="hero-subtitle"
className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-[#9BA1B0] leading-relaxed font-body"
        >
          {t.hero.subtitle}
        </p>

        {/* Buttons */}
        <div
          ref={ctaRef}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <MagneticButton
            data-testid="hero-cta-projects"
            className="btn-primary"
            onClick={() => scrollTo("projects")}
          >
            {t.hero.ctaPrimary}
            <ArrowRight size={18} strokeWidth={2} />
          </MagneticButton>

          <MagneticButton
            data-testid="hero-cta-contact"
            className="btn-outline"
            onClick={() => scrollTo("contact")}
          >
            {t.hero.ctaSecondary}
          </MagneticButton>
        </div>

        {/* Scroll indicator */}
    
      </div>

      {/* Bottom light */}
   

          <button
          onClick={() => scrollTo("about")}
          data-testid="hero-scroll-down"
          className="absolute left-1/2 -translate-x-1/2 bottom-2 text-[#9BA1B0] hover:text-white transition-colors flex flex-col items-center gap-2 group"
          aria-label="Scroll down"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">
            {t.hero.scroll}
          </span>

          <ArrowDown
            size={18}
            className="animate-bounce"
          />
        </button>
    </section>
  );
};

export default Hero;